import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { type Annotation } from '@/components'
import { useLanguage } from '@/contexts/LanguageContext'
import { useNotifications } from '@/contexts/NotificationContext'
import { ApiError } from '@/services/apiError'
import { logError, responseErrorMessage, toUserMessage } from '@/utils/errorMessage'
import { turbineService, type Turbine, type Photo, type PhotoVersion } from '@/services/turbineService'
import { authService } from '@/services/authService'

export const useOperatorPhotosViewModel = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t, language } = useLanguage()
  const { notifySuccess, notifyError, notifyWarning } = useNotifications()

  // --- UiState ---
  const [turbine, setTurbine] = useState<Turbine | null>(null)
  const [loading, setLoading] = useState(true)
  // Yukleme hatasi bos veriden AYRI tutulur: turbin gelmediyse ekran "bulunamadi"
  // demek yerine gercek nedeni ve "Tekrar dene" secenegini gosterir.
  const [loadError, setLoadError] = useState<string | null>(null)
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null)
  const [visiblePhotos, setVisiblePhotos] = useState<Photo[]>([])
  const [isSidebarOpen, _setIsSidebarOpen] = useState(true)
  const [isCommentsOpen, setIsCommentsOpen] = useState(false)
  const [user, setUser] = useState<any>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [refreshSignal, setRefreshSignal] = useState<number>(0)
  const [imageVersion, setImageVersion] = useState<number>(Date.now())

  // Bildirimler artik uygulama capindaki kuyruklu toast katmanindan gecer.
  // Onceki ekrana ozel ToastNotification tek slotluydu: ardisik iki bildirim
  // birbirini eziyor, ikincisi hic gorunmuyordu.
  const showToastNotification = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    if (type === 'error') {
      notifyError(t.errors.title, message)
      return
    }
    notifySuccess(message)
  }, [notifyError, notifySuccess, t])

  // Photo versioning states
  const [photoVersions, setPhotoVersions] = useState<PhotoVersion[]>([])
  const [currentVersionIndex, setCurrentVersionIndex] = useState<number>(0)
  const [loadingVersions, setLoadingVersions] = useState(false)
  // Versiyon listesi yuklenemediginde sessizce bos liste gostermek yerine
  // nedeni + tekrar deneme imkani sunulur.
  const [versionsError, setVersionsError] = useState<string | null>(null)

  const getVersionLabel = useCallback((version: PhotoVersion | undefined, index: number) => {
    if (!version) return 'Versiyon'
    if (version.is_original || index === 0) return 'Orijinal'
    const versionNumber = version.version_number ?? index
    return `Düzenleme ${versionNumber}`
  }, [])

  const _formatVersionMeta = useCallback((version: PhotoVersion) => {
    const parts: string[] = []
    if (version.created_by) parts.push(version.created_by)
    if (version.created_at) {
      const createdDate = new Date(version.created_at)
      if (!isNaN(createdDate.getTime())) {
        // Format: "21 Aralık 2025 - 14:50"
        const formattedDate = createdDate.toLocaleDateString('tr-TR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        })
        const timeString = createdDate.toLocaleTimeString('tr-TR', {
          hour: '2-digit',
          minute: '2-digit'
        })
        parts.push(`${formattedDate} - ${timeString}`)
      }
    }
    return parts.join(' • ')
  }, [])

  // Bir fotografin versiyon listesini yukler. handlePhotoSelect ve "Tekrar dene"
  // ayni yolu kullansin diye ayri tutuldu.
  /** Eldeki `photoVersions` listesinin ait oldugu fotograf id'si. */
  const loadedVersionsPhotoId = useRef<number | null>(null)

  const loadPhotoVersions = useCallback(async (photo: Photo) => {
    if (!photo.id || !turbine?.id) return

    // Eldeki versiyon listesi HANGI fotografa ait? Baska bir fotografa gecildiyse
    // liste artik gecersizdir ve temizlenmelidir. Ama AYNI fotograf icin tekrar
    // deniyorsak temizlemek zarar verir: hata dallarindaki kosulsuz
    // `setPhotoVersions([])` yuzunden kullanici "Tekrar dene"ye bastiginda elindeki
    // GECERLI liste siliniyor, dropdown bosaliyor ve currentVersionIndex 0'a duserek
    // gosterilen URL ile indeks tutarsiz hale geliyordu.
    if (loadedVersionsPhotoId.current !== photo.id) {
      setPhotoVersions([])
      setCurrentVersionIndex(0)
    }

    setLoadingVersions(true)
    setVersionsError(null)
    try {
      const response = await turbineService.getPhotoVersions(turbine.id, photo.id)
      if (response.success && response.data) {
        loadedVersionsPhotoId.current = photo.id
        const allVersions = [response.data.original, ...response.data.versions]
        setPhotoVersions(allVersions)

        // Default: show latest version (last in array)
        const latestIndex = allVersions.length - 1
        setCurrentVersionIndex(latestIndex)

        // Update selected photo to show latest version's URL (keep original photo data)
        if (latestIndex > 0) {
          const latestVersion = allVersions[latestIndex]
          if (latestVersion) {
            setSelectedPhoto(prev => {
              const base = prev || photo
              // Only update if URL or key actually changed to prevent unnecessary re-renders
              if (base.url !== latestVersion.photo_url || base.key !== latestVersion.photo_key) {
                return {
                  ...photo, // Use original photo object to preserve all fields including id
                  url: latestVersion.photo_url,
                  key: latestVersion.photo_key,
                  // Liste eslesmesi/gezinme icin taban anahtar. Tekrar denemede
                  // photo zaten versiyon anahtarini tasiyabilir; onceki taban korunur.
                  _originalKey: photo._originalKey || photo.key
                }
              }
              return base
            })
          }
        }
      } else if (response.success) {
        // Gercekten versiyon yok: bos liste DOGRU sonuctur.
        setPhotoVersions([])
        setCurrentVersionIndex(0)
        loadedVersionsPhotoId.current = photo.id
      } else {
        // success:false bir HATADIR: bos liste gibi davranmak kullaniciya
        // "bu fotografin versiyonu yok" yalanini soyler. Eldeki gecerli liste
        // KORUNUR; yalnizca hata bildirilir.
        setVersionsError(responseErrorMessage(response, t, 'Versiyonlar yüklenemedi.')) // TODO(i18n)
      }
    } catch (error) {
      logError('OperatorPhotos.loadPhotoVersions', error)
      // Hata bos veri DEGILDIR: eldeki liste korunur.
      setVersionsError(toUserMessage(error, t, 'Versiyonlar yüklenemedi.')) // TODO(i18n)
    } finally {
      setLoadingVersions(false)
    }
  }, [turbine?.id, t])

  const handleDeleteVersion = useCallback(async (index: number, version: PhotoVersion) => {
    if (!selectedPhoto || !turbine?.id || index === 0) return // Orijinal versiyonu silemesin

    if (!confirm(`"${getVersionLabel(version, index)}" versiyonunu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
      return
    }

    try {
      const response = await turbineService.deletePhotoVersion(turbine.id, selectedPhoto.id!, version.id)

      if (!response.success) {
        // Backend mesaji varsa jenerik metinle EZILMEZ.
        showToastNotification(responseErrorMessage(response, t, 'Versiyon silinirken hata oluştu!'), 'error') // TODO(i18n)
        return
      }

      // Toast notification göster
      showToastNotification('Versiyon başarıyla silindi!', 'success')

      // Versiyonları yeniden yükle. Buradaki hata SILME hatasi degildir; ayri
      // ele alinmazsa kullaniciya "silinemedi" gibi yanlis bilgi gider.
      try {
        const versionResponse = await turbineService.getPhotoVersions(turbine.id, selectedPhoto.id!)
        if (versionResponse.success && versionResponse.data) {
          const freshVersions = [versionResponse.data.original, ...versionResponse.data.versions]
          setPhotoVersions(freshVersions)
          setVersionsError(null)

          // Eğer silinen versiyon aktifse, bir önceki versiyona geç
          if (index === currentVersionIndex && freshVersions.length > 0) {
            const newIndex = Math.min(index - 1, freshVersions.length - 1)
            setCurrentVersionIndex(newIndex)

            const newVersion = freshVersions[newIndex]
            if (newVersion) {
              setSelectedPhoto({
                ...selectedPhoto,
                url: newVersion.photo_url,
                key: newVersion.photo_key,
                _originalKey: selectedPhoto._originalKey || selectedPhoto.key
              })
              setImageVersion(Date.now())
            }
          }
        } else {
          const message = responseErrorMessage(versionResponse, t, 'Versiyon listesi yenilenemedi.') // TODO(i18n)
          setVersionsError(message)
          notifyWarning('Liste yenilenemedi', message) // TODO(i18n)
        }
      } catch (refreshError) {
        logError('OperatorPhotos.refreshVersionsAfterDelete', refreshError)
        const message = toUserMessage(refreshError, t, 'Versiyon listesi yenilenemedi.') // TODO(i18n)
        setVersionsError(message)
        notifyWarning('Liste yenilenemedi', message) // TODO(i18n)
      }
    } catch (error) {
      logError('OperatorPhotos.deletePhotoVersion', error)
      showToastNotification(toUserMessage(error, t, 'Versiyon silinirken hata oluştu!'), 'error') // TODO(i18n)
    }
  }, [selectedPhoto, turbine?.id, currentVersionIndex, getVersionLabel, showToastNotification, notifyWarning, t])

  const addCacheBust = useCallback((url?: string | null, version?: number) => {
    if (!url) return url || ''
    try {
      const parsed = new URL(url)
      parsed.searchParams.set('cb', (version || Date.now()).toString())
      return parsed.toString()
    } catch (error) {
      const separator = url.includes('?') ? '&' : '?'
      return `${url}${separator}cb=${version || Date.now()}`
    }
  }, [])

  const loadData = useCallback(async () => {
    if (!id) return

    setLoading(true)
    setLoadError(null)
    try {
      const [turbineResponse, userData] = await Promise.all([
        turbineService.getTurbine(parseInt(id)),
        authService.getCurrentUser()
      ])

      if (turbineResponse.success && turbineResponse.turbine) {
        setTurbine(turbineResponse.turbine)
      } else {
        // Mevcut turbin verisi KORUNUR; sadece neden gosterilir.
        setLoadError(responseErrorMessage(turbineResponse, t, t.errors.loadFailed))
      }
      setUser(userData)
    } catch (error) {
      logError('OperatorPhotos.loadData', error)
      setLoadError(toUserMessage(error, t, t.errors.loadFailed))
    } finally {
      setLoading(false)
    }
  }, [id, t])

  useEffect(() => {
    loadData()
    // Yalnizca turbin degistiginde yeniden yuklenir. loadData kimligi dil
    // secimi degisince de degisir; bu tam bir yeniden yuklemeyi tetiklemesin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handlePhotoSelect = useCallback(async (photo: Photo) => {
    setSelectedPhoto(photo)
    // Note: imageVersion is intentionally NOT bumped here. Bumping it on every
    // selection added a fresh "cb" cache-buster to the proxy URL, forcing a full
    // re-download even when re-selecting an already-viewed photo. It is only
    // bumped after a new version is saved or a version is switched/deleted.

    // Load versions for this photo
    if (photo.id && turbine?.id) {
      await loadPhotoVersions(photo)
    } else if (!photo.id) {
      console.warn('⚠️ Photo has no ID:', photo)
    }
  }, [turbine?.id, loadPhotoVersions])

  const retryLoadVersions = useCallback(() => {
    if (!selectedPhoto) return
    loadPhotoVersions(selectedPhoto)
  }, [selectedPhoto, loadPhotoVersions])

  const dismissVersionsError = useCallback(() => setVersionsError(null), [])

  const handlePhotosLoaded = useCallback((photos: Photo[]) => {
    if (!selectedPhoto) return
    const baseKey = selectedPhoto._originalKey || selectedPhoto.key
    const updated = photos.find(p => p.key === baseKey)
    // Only swap when the entry actually changed; keeps version-swapped URLs intact
    if (updated && updated.url !== selectedPhoto.url && !selectedPhoto._originalKey) {
      setSelectedPhoto(updated)
    }
  }, [selectedPhoto])

  // Version URLs are freshly generated when the photo is selected (6h validity),
  // so switching versions never needs another API round-trip.
  const handleVersionChange = useCallback((index: number) => {
    setCurrentVersionIndex(index)

    const version = photoVersions[index]
    if (version && selectedPhoto && selectedPhoto.url !== version.photo_url) {
      setSelectedPhoto({
        ...selectedPhoto,
        url: version.photo_url,
        key: version.photo_key,
        _originalKey: selectedPhoto._originalKey || selectedPhoto.key
      })
      setImageVersion(Date.now()) // Sadece URL değiştiğinde yeniden yükle
    }
  }, [photoVersions, selectedPhoto])

  const handleSaveEditedPhoto = useCallback(async (imageBlob: Blob, _annotations: Annotation[]) => {
    if (!turbine || !selectedPhoto || !selectedPhoto.id) {
      // Duz `new Error(...)` firlatilirsa metin KAYBOLUR: toApiError ham JS
      // hatalarinin mesajini kullaniciya sizdirmadigi icin goruntuleyici jenerik
      // "Fotograf kaydedilemedi" gosterir ve ozgul sebep hic gorunmez.
      throw new ApiError('validation', {
        serverMessage: language === 'tr' ? 'Kaydedilecek fotoğraf bulunamadı' : 'No photo selected to save'
      })
    }

    setIsSaving(true)

    try {
      // Direct-to-Wasabi upload (presign → PUT → confirm). The previous
      // multipart POST went through the Vercel function, whose 4.5MB body
      // limit made saving large edited photos fail.
      const response = await turbineService.saveEditedPhotoVersion(
        turbine.id,
        selectedPhoto.id,
        imageBlob,
        imageBlob.type || 'image/jpeg'
      )

      if (!response.success) {
        // Duz `new Error(...)` firlatmak backend mesajini KAYBETTIRIR: toApiError
        // ham JS hatalarinin metnini kullaniciya sizdirmadigi icin mesaj jenerik
        // metne duserdi. ApiError ile mesaj hem burada hem goruntuleyicide korunur.
        throw new ApiError('unknown', {
          serverMessage: responseErrorMessage(response, t, 'Fotoğraf güncellenemedi'), // TODO(i18n)
        })
      }

      // Show toast notification for immediate feedback
      showToastNotification(
        language === 'tr'
          ? 'Fotoğraf başarıyla düzenlendi ve yeni versiyon olarak kaydedildi!'
          : 'Photo successfully edited and saved as new version!',
        'success'
      )

      // Immediately reload versions to show the new version in dropdown
      if (selectedPhoto.id) {
        try {
          const versionsResponse = await turbineService.getPhotoVersions(turbine.id, selectedPhoto.id)
          if (versionsResponse.success && versionsResponse.data) {
            const allVersions = [versionsResponse.data.original, ...versionsResponse.data.versions]
            setPhotoVersions(allVersions)
            setVersionsError(null)

            // Auto-select the newly created version (last one)
            const latestIndex = allVersions.length - 1
            setCurrentVersionIndex(latestIndex)

            // Update selected photo to show new version
            const latestVersion = allVersions[latestIndex]
            if (latestVersion) {
              setSelectedPhoto({
                ...selectedPhoto,
                url: latestVersion.photo_url,
                key: latestVersion.photo_key,
                _originalKey: selectedPhoto._originalKey || selectedPhoto.key
              })
            }
            setImageVersion(Date.now())
          } else {
            // Kayit BASARILI ama liste tazelenemedi: aksi halde ekranda eski
            // versiyon gorunur ve kullanici bunu fark etmez.
            const message = responseErrorMessage(versionsResponse, t, 'Versiyon listesi yenilenemedi.') // TODO(i18n)
            setVersionsError(message)
            notifyWarning('Liste yenilenemedi', message) // TODO(i18n)
          }
        } catch (versionError) {
          logError('OperatorPhotos.reloadVersionsAfterSave', versionError)
          const message = toUserMessage(versionError, t, 'Versiyon listesi yenilenemedi.') // TODO(i18n)
          setVersionsError(message)
          notifyWarning('Liste yenilenemedi', message) // TODO(i18n)
        }
      }

      setRefreshSignal(Date.now())
    } catch (error) {
      logError('OperatorPhotos.saveEditedPhoto', error)
      // TEK KAYNAK: kaydetme hatasini ImageViewerWithAnnotations kendi kutusunda
      // gosterir (hem normal hem tam ekran modda). Burada ayrica bir ekran banner'i
      // doldurmak kullaniciya AYNI metni iki kez gosteriyor ve iki kez kapattiriyordu.
      // Hata oldugu gibi yeniden firlatilir ki goruntuleyici backend mesajini gorsun.
      throw error
    } finally {
      setIsSaving(false)
    }
  }, [language, selectedPhoto, turbine, showToastNotification, notifyWarning, t])

  const handleImageError = useCallback(() => {
    setRefreshSignal(Date.now())
    setImageVersion(Date.now())
  }, [])

  // Prev/next navigation over the photos currently visible in the sidebar
  // (used by the fullscreen viewer arrows + keyboard).
  const handleNavigate = useCallback((direction: 'prev' | 'next') => {
    if (visiblePhotos.length === 0) return

    const baseKey = selectedPhoto?._originalKey || selectedPhoto?.key
    const currentIndex = visiblePhotos.findIndex(p => p.key === baseKey)
    const len = visiblePhotos.length
    const nextIndex = currentIndex === -1
      ? 0
      : direction === 'next'
        ? (currentIndex + 1) % len
        : (currentIndex - 1 + len) % len

    const nextPhoto = visiblePhotos[nextIndex]
    if (nextPhoto) {
      handlePhotoSelect(nextPhoto)
    }
  }, [visiblePhotos, selectedPhoto, handlePhotoSelect])

  const logout = async () => {
    try {
      await authService.logout()
    } catch (error) {
      // Cikis sunucuda basarisiz olsa da kullanici disari cikarilir; yine de
      // sessiz kalmamak icin bilgilendirilir.
      logError('OperatorPhotos.logout', error)
      notifyWarning(t.errors.title, toUserMessage(error, t, t.errors.generic))
    } finally {
      navigate('/')
    }
  }

  return {
    // state
    turbine,
    loading,
    loadError,
    selectedPhoto,
    visiblePhotos,
    setVisiblePhotos,
    isSidebarOpen,
    isCommentsOpen,
    setIsCommentsOpen,
    user,
    isSaving,
    refreshSignal,
    imageVersion,
    photoVersions,
    currentVersionIndex,
    loadingVersions,
    versionsError,
    // actions
    reloadData: loadData,
    handlePhotoSelect,
    handlePhotosLoaded,
    handleVersionChange,
    handleDeleteVersion,
    handleSaveEditedPhoto,
    handleImageError,
    handleNavigate,
    retryLoadVersions,
    dismissVersionsError,
    addCacheBust,
    logout,
  }
}
